
import { LoadState } from '@/utils/types';
import { MaintenanceItem, TripLog } from '@clients/myTrips';
import ChevronRightIcon from '@components/icons/icon-chevron-right.svg';
import AppContent from '@components/new-styleguide/AppContent.vue';
import AppHeader from '@components/new-styleguide/AppHeader.vue';
import AppListItem, { ListItem } from '@components/new-styleguide/AppListItem.vue';
import AppListItemPlaceholder from '@components/new-styleguide/AppListItemPlaceholder.vue';
import AppPage from '@components/new-styleguide/AppPage.vue';
import VehicleSelect from '@components/new-styleguide/form/VehicleSelect.vue';
import GlobalVehicleSelect from '@components/new-styleguide/GlobalVehicleSelect.vue';
import InfoBoxWarning from '@components/shared/ui/InfoBoxWarning.vue';
import { RouterQuery } from '@router/Routes';
import Triplog from '@store/modules/Triplog';
import { deepEquals } from '@util/copy';
import getMappedItem from '@util/maintenanceItemMapper';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const SettingsModule = namespace('Settings');
const TriplogModule = namespace('Triplog');
const RouterModule = namespace('Router');

@Component({
  name: 'MaintenanceOverview',
  components: {
    GlobalVehicleSelect,
    AppListItemPlaceholder,
    VehicleSelect,
    AppListItem,
    InfoBoxWarning,
    AppContent,
    AppPage,
    AppHeader,
    ChevronRightIcon
  }
})
export default class MaintenanceOverview extends Vue {
  @SettingsModule.Getter('getMaintenanceItemsStatus')
  private maintenanceItemStatus: LoadState;

  @SettingsModule.Getter('getMaintenanceItems')
  private maintenanceItems: MaintenanceItem[];

  @SettingsModule.Action('fetchMaintenanceItems')
  private fetchMaintenanceItems;

  @SettingsModule.Action('updateMaintenanceItemActive')
  private updateMaintenanceItemActive;

  @TriplogModule.Getter('getVehiclesStatus')
  private vehicleStatus: LoadState;

  @TriplogModule.Getter('isGetVehiclesError')
  private isVehiclesError: boolean;

  @TriplogModule.Action('fetchLatestTrip')
  private fetchLatestTrip;

  @RouterModule.State('query')
  private query: RouterQuery;

  @TriplogModule.Getter('getSelectedTriplog')
  private selectedTriplog: TripLog;

  @TriplogModule.Getter('getVehiclesStatus')
  private triplogsStatus: LoadState;

  async created() {
    await Promise.all([ this.maintenanceItemStatus != LoadState.Success ? this.fetchMaintenanceItems(): null, this.fetchLatestTrip]);
  }

  private toggleMaintenanceItem(id: string, active: boolean) {
    this.updateMaintenanceItemActive({ vehicleId: this.selectedTriplog.vehicle.id, id, active });
  }

  get items() {
    if (this.isLoading) {
      return [];
    }

    return [...this.maintenanceItems]
      .filter(m => m.vehicleId === this.selectedTriplog.vehicle.id)
      .map(m => getMappedItem(m, this.selectedTriplog.vehicle.id))
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  get backButton() {
    return { text: 'Zurück zu Anpassen', location: { name: 'mySettings' } };
  }

  get addButton() {
    return {
      text: 'Neue Erinnerung anlegen',
      location: { name: 'maintenanceAdd', query: this.selectedTriplog ? { vehicleId: String(this.selectedTriplog.vehicle.id) } : null }
    }
  }

  get headerText() {
    return 'Erinnerungen helfen Ihnen dabei, den Gesundheits-Zustand Ihres Fahrzeuges im Blick zu behalten. Sie können' +
      'eigene Erinnerungen erstellen, oder bestehende (wie "Ölstand prüfen") anpassen.';
  }

  get isLoading() {
    return this.maintenanceItemStatus === LoadState.Loading || this.vehicleStatus === LoadState.Loading;
  }

  get errorLoading() {
    return this.maintenanceItemStatus === LoadState.Error || this.isVehiclesError;
  }

  get createItem(): ListItem {
    return {
      title: 'Neue Erinnerung anlegen',
      tagIcon: { value: 'tag_plus', color: '#FFDC00', fill: 'black' },
      location: { name: 'maintenanceAdd', query: this.selectedTriplog ? { vehicleId: String(this.selectedTriplog.vehicle.id) } : null }
    };
  }

  get triplogsLoaded() {
    return this.triplogsStatus === LoadState.Success;
  }
}
